
class ContractsMessages {
  readonly UNAVAILABLE_CONTRACT =
    'No available contracts to edit linking options for. Upload a new contract by clicking on the Upload New Contract button or edit linking options directly in the contracts table for contracts created from this shell.';

  readonly UNAVAILABLE_CONTRACT_IRONCLAD =
    'No available contracts to edit linking options for. Create a new contract by clicking on the Create New Contract button.';

  readonly CLOSED_MAIN_BUDGET = 'The main budget is closed';
  readonly NO_PERMISSION =
    'You do not have permission to access this page. Please contact imanagesupport@protiviti.com for assistance.';
  readonly DISABLED_CONTRACT_UNLINK_TOOLTIP = 'This contract cannot be Unlinked due to one of the following reasons:\n' +
    '- Contract has been validated for budget activation.\n' +
    '- The associated iManage Shell and sub-budgets have been marked closed.\n' +
    '- This parent contract has linked amendments. Remove Amendments to Unlink parent contract.\n' +
    '- Contract was created via integration with Ironclad.';
}

class UploadMessages {
  readonly SUBMITTION_IN_PROGRESS = 'Submission in progress, please wait...';
  readonly MAX_10_FILES = 'Only a maximum of ten files can be uploaded.';
  readonly ALREADY_UPLOADED_FILE =
    'This file has already been uploaded for this account / client. Rename the file or delete and upload a new file.';
  readonly DOCUMENT_NAME_REQUIRED = 'Name is required.';
  readonly DOCUMENT_NAME_INVALID_CHARACTERS = 'File names cannot contain the following characters: " * : ; < > ? / \\ | #';
  readonly DOCUMENT_NAME_INVALID_RESERVED = 'The following file names are invalid: .lock, CON, PRN, AUX, NUL, COM1 - COM9, LPT1 - LPT9, _vti_, desktop.ini, any filename starting with ~$.';
  readonly DOCUMENT_NAME_EXISTS = 'This file name has already been used for this account / client. Rename the file or delete and upload a new file.';
  readonly DOCUMENT_NAME_DUPLICATE = 'A file name cannot match another document name being uploaded.';
  readonly BLOCKED_FILE_TYPES_DETAILED =
    'The following file types cannot be uploaded: *.exe, *.bat, *.js, .emltpl, .log, .OFT, .P7m, .TIFF, .xltx, .zip, or any script file.';
  readonly NO_FILE_UPLOADED_SUCCESSFULLY = 'No file was uploaded successfully.';

  FILE_WITHOUT_EXTENSION(fileName: string): string {
    return `Cannot upload contract "${fileName}" which does not have file extension.`;
  }

  VIDEO_FILE_NOT_ALLOWED(fileName: string): string {
    return `${fileName} could not be uploaded: Video files cannot be attached.`;
  }

  FILE_RESTRICTION_FAILURE(fileName: string, reason: string): string {
    return `${fileName} could not be uploaded: ${reason}`;
  }

  MAX_TOTAL_FILE_SIZE_LIMIT(fileName: string): string {
    return `${fileName} could not be uploaded: The maximum total file size limit is 100MB.`;
  }

  UNSUPPORTED_FILE_TYPE_DETECTED(currentType: string, rejectedTypes: string, supportedTypes: string): string {
    return `Unsupported file type detected (${currentType}). ` +
      `Please stop uploading these types. Previously rejected types: ${rejectedTypes}. ` +
      `Ironclad supported files: ${supportedTypes}.`;
  }
}

class ApiMessages {
  readonly ERROR_SUBMITTING_REQUEST =
    'There was an error submitting your request. Please contact iManageSupport@protiviti.com for assistance.';
}

class AppMessages {
  readonly ERROR_OCCURRED =
    'An error has occurred.  If the issue persists, please contact iManageSupport@protiviti.com for assistance.';
  readonly REQUEST_ACCESS =
    'Please contact the Engagement Team to request access. If you need further assistance, please contact Technical Support.';
  readonly PERMISSION_NOT_PROVIDED = 'Permission to access iManage has not been provided.  Please contact iManageSupport@protiviti.com for assistance.';
}

class EngagementMessages {
  readonly ENGAGEMENT_NOT_FOUND = 'Engagement not found.';
  readonly REQUEST_ACCESS =
    'Please contact the Engagement Team to request access. If you need further assistance, please contact iManageSupport@protiviti.com.';
  readonly DELETED_RECORD =
    'This record has been deleted. Please contact the Engagement Team or iManageSupport@protiviti.com for assistance.';
  readonly ERROR_500 =
    'Oops! Something went wrong. Please contact iManageSupport@protiviti.com.';
  readonly ERROR_500_TO_600 =
    'Oops!  Something went wrong. Please refresh the page and try again. If the issue continues, please contact iManageSupport@protiviti.com.';
  readonly UNLINK_SUBMITTED_APPROVED_ERA =
    'Please unlink the Submitted/Approved ERA before updating the Client to a company from a different Client Hierarchy (i.e., with a different Parent Client).';
  readonly NO_PERMISSION_TO_CREATE_SHELL =
    'You do not have permission to create a shell.  Please contact imanagesupport@protiviti.com for assistance.';
  readonly NO_LINKED_SALESFORCE_ACCOUNT = 'The Account / Client you attempted to select does not have a Salesforce Account linked. Contact iManageSupport@protiviti.com for assistance. ​To save other updates to your form, click the Save button.';
  readonly INVALID_LEAD_ECOSYSTEM_SELECTION = 'Invalid combination of Lead Source and Ecosystem selection. An Ecosystem Partner value must be selected for both the Lead Source and Ecosystem fields.';
  readonly REQUIRED_FORM_FIELDS = 'Name, BU, Opportunity / Contract Currency, Opportunity MD / EMD and Opportunity Owner / EM are required to save the form.';
  NO_CONTACT_SELECTED(salesForceUrl: string) : string {
    return `A Contact must be selected as the Primary Key Buyer before closing an opportunity. Navigate to the <a href="${salesForceUrl}" target="_blank">Salesforce Account</a> to add or update Contacts made available in iManage.`;
  }
  readonly ACCOUNT_CLIENT_CANT_BE_UPDATED = 'Account / Client cannot be updated at this Opportunity Stage. Please update the Opportunity Stage to "Potential Opportunity Identified" before updating the Account / Client.';
  readonly FORM_SAVED_WITH_MISSING_FIELDS = 'Form saved with missing fields. The fields highlighted in red are required to progress with opportunity/engagement setup.';
  readonly REVENUE_SCHEDULES_MUST_BE_COMPLETED = 'All Revenue Schedules must be completed to update the Opportunity Stage. Please navigate to the Services section to complete all Revenue Schedules.';
  readonly PLEASE_COMPLETE_ALL_REQUIRED_FIELDS = 'Please complete all required fields to save the form.';
}

class FormMessages {
  readonly FORM_NO_ACCESS =
    'You do not have permission to access to this form.  If you need further assistance, please contact iManageSupport@protiviti.com.';
  readonly FORM_REQUIRED_RED_FIELDS =
    'Fields highlighted in red below are required for submission.';
  readonly COMPLETE_ALL_REQUIRED_FIELDS =
    'Please complete all required fields to save the form.';
}

export class ErrorMessagesConstants {
  static readonly APP_MESSAGES: AppMessages = new AppMessages();
  static readonly ENGAGEMENT_MESSAGES: EngagementMessages = new EngagementMessages();
  static readonly FORM_MESSAGES: FormMessages = new FormMessages();
  static readonly API_MESSAGES: ApiMessages = new ApiMessages();
  static readonly UPLOAD_MESSAGES: UploadMessages = new UploadMessages();
  static readonly CONTRACTS_MESSAGES: ContractsMessages = new ContractsMessages();

  static getValidationMessages(componentName: string): any {
    if (!componentName || componentName === '') {
      return null;
    }
    switch (componentName) {
      case 'shellEdit':
        return {
          clientSelect: {
            required: 'clientSelect is required.',
          },
          name: {
            required: 'opportunityEngagementName name is required.',
            minlength:
              'opportunityEngagementName name must be at least three characters.',
            maxlength:
              'opportunityEngagementName name cannot exceed 50 characters.',
          },
          leadEMDSelect: {
            required: 'leadEMD is required.',
          },
          leadEMSelect: {
            required: 'leadEMD is required.',
          },
          departmentSelect: {
            required: 'projectDepartment is required.',
          },
          serviceOfferingSelect: {
            required: 'serviceOffering is required.',
          },
          serviceAmount: {
            required: 'serviceAmount is required.',
          },
          opportunityMDSelect: {
            required: 'opportunityMD is required.',
          },
          opportunityOwnerSelect: {
            required: 'opportunity owner is required.',
          },
        };
      case 'shellLinkExisting':
        return {
          name: {
            required: 'opportunityEngagementName name is required.',
            minlength:
              'opportunityEngagementName name must be at least three characters.',
            maxlength:
              'opportunityEngagementName name cannot exceed 50 characters.',
          },
          opportunityCurrencySelect: {
            required: 'Opportunity/Contract Currency is required.',
          },
          leadEMDSelect: {
            required: 'leadEMD is required.',
          },
          leadEMSelect: {
            required: 'leadEMD is required.',
          },
          departmentSelect: {
            required: 'projectDepartment is required.',
          },
        };
      case 'pkicRequestForm':
        return {
          name: {
            required: 'opportunityEngagementName name is required.',
            minlength:
              'opportunityEngagementName name must be at least three characters.',
            maxlength:
              'opportunityEngagementName name cannot exceed 50 characters.',
          },
          requestDeliveryDate: {
            required: 'Request Delivery Date is required.',
          },
          effortEstimateDisplay: {
            required: 'Effort Estimate is required.',
          },
          billableDisplay: {
            required: 'Billable is required.',
          },
          descriptionDisplay: {
            required: 'Description is required',
          },
          engagementIdDisplay: {
            required: 'Engagement ID is required',
          },
        };
      default:
        throw new Error('Invalid component name provided: ' + componentName);
    }
  }
}
